import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { NBSP, PRODUCT_TYPE_LABEL_MAP } from 'src/settings/settings';
import {
  isExchangeAgreementType,
  isExchangeFundType,
  isExchangeLimit,
  isExchangeOpeningTypes,
} from 'src/utils/funds/funds-utility';
import { currencyNoPrecision } from 'src/utils/utils';
import { ApplicationState } from '../types';
import { ReducedTransactionLimit, TransactionLimit } from './types';

const columns = [
  'Account Type',
  'Fund Type',
  `Minimum (To${NBSP}New)`,
  `Minimum (To${NBSP}Existing)`,
  'Maximum',
];

const slice = ({ transactionLimits }: ApplicationState) => transactionLimits;
/**
 * Pull the TransactionLimits object from the store.
 */
export const getTransactionLimits = createSelector(
  [slice],
  ({ transactionLimits }) => transactionLimits
);

/**
 * Pull out just the data that will be displayed in the table.
 * Also pull out openingType, which will be needed to merge two objects into one row later.
 *
 * @param {TransactionLimit} limitObject
 * @returns {typeof ReducedTransactionLimit}
 */
const formatFlattenedLimit = ({
  maximum,
  minimum,
  agreementTypeCategory,
  fundType,
  openingType,
}): ReducedTransactionLimit => ({
  agreementTypeCategory:
    agreementTypeCategory === 'UGMA_AND_UTMA'
      ? 'Custodial/UTMA'
      : agreementTypeCategory,
  fundType,
  maxFormatted: currencyNoPrecision(maximum),
  minFormatted: currencyNoPrecision(minimum),
  openingType,
});

/**
 * Merge all common values from the two objects in the agreementGroup,
 * but keep the different minimums based on the account.
 *
 * @param {ReducedTransactionLimit} a
 * @param {ReducedTransactionLimit} b
 * @returns {typeof TransactionLimitRow}
 */
const mergeSameAgreementObjects = (
  a: ReducedTransactionLimit,
  b: ReducedTransactionLimit
) => [
  a.agreementTypeCategory,
  a.fundType,
  a.openingType === 'NewAccount' ? a.minFormatted : b.minFormatted,
  a.openingType !== 'NewAccount' ? a.minFormatted : b.minFormatted,
  a.maxFormatted,
];

/**
 * Filter out limits not relevant.
 *
 *
 * @param {TransactionLimit[]} transactionLimits
 * @returns {TransactionLimit[]}
 */
const filterTransactionLimitsForExchange = (
  transactionLimits: TransactionLimit[]
) =>
  transactionLimits.filter(
    ({ criteria }) =>
      isExchangeLimit(criteria) &&
      isExchangeAgreementType(criteria.agreementTypeCategory) &&
      isExchangeFundType(criteria.fundType) &&
      isExchangeOpeningTypes(criteria.openingType) &&
      !(
        criteria.agreementTypeCategory.enumerationName === 'UGMA_AND_UTMA' &&
        criteria.fundType.enumerationName === 'IClass'
      ) // A stand alone requirement
  );

/**
 * Pick out the relevant properties from TransactionLimit, and flatten out the `enumerationName`s for a cleaner object.
 *
 * @param {TransactionLimit} * - The full Limit object.
 * @returns {typeof ReducedTransactionLimit}
 */
const pickAndFlattenLimit = ({
  criteria: { agreementTypeCategory, fundType, openingType },
  minimum,
  maximum,
}: TransactionLimit) => ({
  agreementTypeCategory: agreementTypeCategory.enumerationName,
  fundType: fundType.enumerationName,
  maximum,
  minimum,
  openingType: openingType.enumerationName,
});

/**
 * Pick out the purchase limits corresponding to the type of 529 funds the user has, and format it for table display.
 *
 * @param {TransactionLimit[]} transactionLimits
 * @returns {TransactionLimitsTableData}
 */
export const getMutualFundExchangeTransactionLimits = createSelector(
  getTransactionLimits,
  (transactionLimits = []) => {
    // drop out if the input is invalid
    if (isEmpty(transactionLimits)) {
      return { columns, tables: [] };
    }
    // Filter down the list of ~1000 limits to 16
    const filteredLimits =
      filterTransactionLimitsForExchange(transactionLimits);

    // Convert deep limit objects to simple flat objects, with formatted values for the table
    const transformedLimits = filteredLimits.map((limit) =>
      formatFlattenedLimit(pickAndFlattenLimit(limit))
    );

    // every accountType + fundType option has a pair of rows for each of the two possible `openingTypes`, they need to be merged into one row entry
    const accumulator: Record<string, ReducedTransactionLimit | string[]> = {};
    transformedLimits.forEach((limit) => {
      const key: string = limit.agreementTypeCategory + limit.fundType;
      const limitPair = accumulator[key];

      accumulator[key] = limitPair
        ? mergeSameAgreementObjects(limit, limitPair as ReducedTransactionLimit)
        : limit;
    });

    const rows = Object.values(accumulator).sort((a, b) =>
      b[0].localeCompare(a[0])
    );

    return {
      columns,
      tables: [
        {
          label: PRODUCT_TYPE_LABEL_MAP.MUTUAL_FUND + ' Exchange',
          rows,
        },
      ],
    };
  }
);

/**
 * Returns the exchange transaction limits
 * Used for front end field validtion to ensure amounts meet validation criteria
 * @param {TransactionLimit[]} transactionLimits
 * @returns {typeof ReducedTransactionLimit}
 *
 */
export const getExchangeToTransactionLimits = createSelector(
  getTransactionLimits,
  (transactionLimits) => {
    const filteredLimits =
      filterTransactionLimitsForExchange(transactionLimits);
    const transformedLimits = filteredLimits.map((limit) =>
      pickAndFlattenLimit(limit)
    );
    return transformedLimits;
  }
);

/**
 * Returns the exchange transaction limits
 * Used for front end field validtion to ensure amounts meet validation criteria
 * @param {TransactionLimit[]} transactionLimits
 * @returns {typeof ReducedTransactionLimit}
 *
 */
export const getExchangeFromTransactionLimits = createSelector(
  getTransactionLimits,
  (transactionLimits) => {
    const filteredLimits =
      filterTransactionLimitsForExchange(transactionLimits);
    const transformedLimits = filteredLimits.map((limit) =>
      pickAndFlattenLimit(limit)
    );
    return transformedLimits;
  }
);
